import { useEffect, useRef, useState } from "react";
import { repositoryMdm, repositoryPos } from "../../_base/_const/_constVar";
import { MessageResponse } from "../../model/MessageResponse";
import {
  Badge,
  Box,
  Button,
  ComboboxItem,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  Select,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Flex,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { TblOutwardWarrantyVendor } from "../../model/TblOutWard";
import { useDisclosure } from "@mantine/hooks";
import { DatePickerInput } from "@mantine/dates";
import { getValueById } from "../../_base/helper/FunctionHelper";
import {
  getTblDmDeliverPrioritySelect,
} from "../../service/getSelectApi";
import { modals } from "@mantine/modals";
import {
  IconArrowBackUp,
  IconBarcode,
  IconCheck,
  IconListCheck,
  IconPackageExport,
  IconPackageImport,
  IconTruckDelivery,
  IconUser,
} from "@tabler/icons-react";
import React from "react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { mapDetailToVoucherInfo } from "./serviceRepairVoucherDetail";

type ServiceRepairOutWardWarrantyVendorProps = {
  back: any;
  handleVendorWarrantyOutward?: any;
  invId: any;
  dataRowProps: any;
  /** CREATE: lập phiếu mới. DETAIL: xem lại phiếu đã lưu, dùng chung layout, khoá nhập liệu. */
  mode?: "CREATE" | "DETAIL";
  /** Dữ liệu trả về từ API chi tiết, bắt buộc khi mode = DETAIL */
  detail?: any;
};

const ServiceRepairOutWardWarrantyVendor = ({
  back,
  handleVendorWarrantyOutward,
  invId,
  dataRowProps,
  mode = "CREATE",
  detail,
}: ServiceRepairOutWardWarrantyVendorProps) => {
  const isDetail = mode === "DETAIL";
  const [codeSupplier, setCodeSupplier] = useState<string>("");

  const userLogin = JSON.parse(localStorage.getItem("userLogin") || "{}");
  const firstItem = dataRowProps?.[0];

  const entity = {
    createDate: new Date(new Date().getTime() + 7 * 3600 * 1000).toISOString(),
    createBy: Number(userLogin?.id || ""),
    customerId: null,
    customerName: null,
    invId: invId,
    priority: null,
    departmentId: "KHONG",
    telephoneNumber: null,
    deliveryAppointmentTime: new Date(
      new Date().getTime() + 7 * 3600 * 1000,
    ).toISOString(),
    fromBranchAddress: null,
    customerProvinceId: null,
    customerDistrictId: null,
    customerCommuneId: null,
    customerStreetId: null,
    customerAddress: null,
    toAddress: null,
    description: null,
    content: null,
  };

  const form = useForm<TblOutwardWarrantyVendor>({
    mode: "uncontrolled",
    validateInputOnChange: true,
    initialValues: {
      ...entity,
    },

    validate: {
      customerId: (value: number | null) => {
        if (!value) {
          return "Vui lòng nhập tên nhà cung cấp !";
        }
      },
    },
  });

  const [visible, { close, open }] = useDisclosure(false);
  const [isSaved, setIsSaved] = useState(false);

  // Form chạy ở mode "uncontrolled" nên getInputProps chỉ trả defaultValue.
  // Dữ liệu của phiếu đã lưu được nạp sau khi mount nên phải bind bằng value,
  // nếu không ô sẽ hiển thị rỗng.
  const textFieldProps = (name: string) =>
    isDetail
      ? { value: (form.getValues() as any)?.[name] ?? "" }
      : form.getInputProps(name);

  const selectFieldProps = (name: string) =>
    isDetail
      ? { value: (form.getValues() as any)?.[name] ?? null }
      : form.getInputProps(name);

  const [isShipping, setIsShipping] = useState<boolean>(false);
  const [dataVendorSelect, setDataVendorSelect] = useState<any[]>([]);
  const [dataDeliverPriority, setDataDeliverPriority] = useState<
    ComboboxItem[]
  >([]);
  const [outwardInfo, setOutwardInfo] = useState<any>(null);

  const headerRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const formHeight = formRef.current?.offsetHeight || 0;
      setHeight(window.innerHeight - (headerHeight + formHeight + 130));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getOutwardInfo = async () => {
    try {
      const res = await repositoryPos.get<MessageResponse<any>>(
        `/api/v1/Outward/get-service-vendor-warranty-outward`
      );
      if (res?.success) {
        setOutwardInfo(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    // Ở chế độ xem chi tiết, mã phiếu / người lập lấy từ phiếu đã lưu
    // chứ không sinh mã mới.
    if (isDetail) return;
    getOutwardInfo();
  }, [isDetail]);

  // Đổ dữ liệu phiếu đã lưu vào đúng các ô của màn tạo mới
  useEffect(() => {
    if (!isDetail || !detail) return;

    setOutwardInfo(mapDetailToVoucherInfo(detail));
    setCodeSupplier(detail.vendorCode ?? "");

    if (detail.vendorId) {
      setDataVendorSelect([
        {
          value: detail.vendorId.toString(),
          label: [detail.vendorCode, detail.vendorName]
            .filter((i: any) => i && String(i).length > 0)
            .join(" - "),
          addr: detail.vendorAddress ?? "",
          phone: detail.vendorPhone ?? "",
          code: detail.vendorCode ?? "",
        },
      ]);
    }

    const delivery = detail.deliveryInfo ?? {};

    form.setValues((prev: any) => ({
      ...prev,
      createDate: detail.createDate ?? prev.createDate,
      description: detail.description ?? "",
      // Select của Mantine so khớp value dạng chuỗi với option
      customerId: detail.vendorId != null ? detail.vendorId.toString() : null,
      customerName: detail.vendorName ?? null,
      telephoneNumber: delivery.telephoneNumber ?? detail.vendorPhone ?? null,
      toAddress: delivery.toAddress ?? detail.vendorAddress ?? null,
      departmentId: delivery.deliveryDepartment ?? "KHONG",
      priority: delivery.priority ?? null,
      content: delivery.content ?? "",
    }));
  }, [isDetail, detail]);

  // Danh mục mức độ ưu tiên chỉ nạp khi thực sự cần hiển thị nhãn
  useEffect(() => {
    if (!isDetail || !detail?.deliveryInfo?.priority) return;
    getDataDeliverPrioritySelect();
  }, [isDetail, detail]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: "Mã sản phẩm",
        Cell: ({ row }) =>
          row.original.newItemCode || row.original.itemCode,
        size: 130,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "itemName",
        header: "Tên sản phẩm",
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "serial",
        header: "Serial",
        Cell: ({ row }) =>
          row.original.newSerial || row.original.serial,
        size: 150,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "attribute16",
        header: "Nhà cung cấp",
        Cell: ({ row }) => row.original.attribute16 || "-",
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "serialStatus",
        header: "Trạng thái serial",
        size: 120,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "subInvName",
        header: "Kho",
        Cell: ({ row }) => (
          row.original.subInvCode && row.original.subInvName
            ? `${row.original.subInvCode} - ${row.original.subInvName}`
            : row.original.subInvName || row.original.invOrgName || ""
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "postionCode",
        header: "Vị trí",
        Cell: ({ row }) => (
          row.original.postionCode && row.original.positionname
            ? `${row.original.postionCode} - ${row.original.positionname}`
            : row.original.postionCode || ""
        ),
        size: 100,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
      },
    ],
    [dataRowProps],
  );

  const getDataVendor = async () => {
    const res = await repositoryMdm.get<MessageResponse<any>>(
      `/api/v1/TblVendor/get-all`,
    );

    if (res && res?.success) {
      const dataApi = res.data;
      setDataVendorSelect(
        dataApi
          ?.filter(
            (item: any) =>
              item.vendorName &&
              item.id &&
              item.vendorCode &&
              !item.vendorCode?.toString().startsWith("BH_HNC"),
          )
          ?.map((item: any) => ({
            value: item.id?.toString(),
            label: `${item.vendorCode} - ${item.vendorName}`,
            addr: item.addr?.toString(),
            phone: item.phone?.toString(),
            code: item.vendorCode?.toString(),
          })),
      );
    }
  };

  const getDataDeliverPrioritySelect = async () => {
    const res = await getTblDmDeliverPrioritySelect();
    setDataDeliverPriority(
      res
        ?.filter((item: any) => item.value != null && item.text != null)
        ?.map((item: any) => ({
          value: item.value,
          label: item.text,
        })),
    );
  };

  const table = useMantineReactTable({
    columns,
    data: dataRowProps,
    positionToolbarAlertBanner: "bottom",
    enableTopToolbar: false,
    renderTopToolbarCustomActions: ({ table }) => <></>,
    renderToolbarInternalActions: ({ table }) => <></>,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "1px solid #e2e8f0",
        marginBottom: 5,
      },
    },
    mantinePaperProps: {
      shadow: "0",
      withBorder: false,
    },
    enableRowSelection: false,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select", "itemCode"],
        right: [],
      },
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height, borderRadius: "6px", border: "1px solid #e2e8f0" },
    },
    enableStickyHeader: true,
    manualFiltering: false,
    manualPagination: true,
    manualSorting: false,
    mantineTableHeadCellProps: {
      style: {
        backgroundColor: "#f8fafc",
        color: "#334155",
        fontWeight: 700,
        fontSize: "12.5px",
        borderBottom: "2px solid #cbd5e1",
        padding: "8px 12px",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        fontSize: "12.5px",
        padding: "8px 12px",
      },
    },
    mantineToolbarAlertBannerProps: false
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    enablePagination: false,
    enableBottomToolbar: false,
    enableColumnPinning: true,
    mantineTableProps: {
      striped: "even",
      highlightOnHover: true,
      withColumnBorders: true,
      withRowBorders: true,
      withTableBorder: true,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
  });

  return (
    <Box
      component="form"
      mx="auto"
      onSubmit={form.onSubmit((e: TblOutwardWarrantyVendor) => {
        modals.openConfirmModal({
          title: "Xác nhận xuất kho bảo hành",
          centered: true,
          children: (
            <div style={{ paddingBottom: 24, paddingTop: 5 }}>
              <Text size="lg" fw={600} ta="center" mb={8}>
                Bạn có chắc chắn các thông tin đã nhập là chính xác?
              </Text>
              <Text component="div" ta="center" size="sm" fs="italic" c="red" fw={500}>
                *Lưu ý: Phiếu xuất sẽ không thể sửa khi ấn "Đồng ý".
              </Text>
            </div>
          ),
          labels: { confirm: "Đồng ý", cancel: "Hủy" },
          onConfirm: async () => {
            open();
            try {
              const success = await handleVendorWarrantyOutward(
                e,
                isShipping,
                codeSupplier && codeSupplier.length > 0 ? codeSupplier.trim() : null,
                e.description && e.description.length > 0
                  ? e.description.trim()
                  : null,
                outwardInfo?.transactionCode
              );
              if (success) setIsSaved(true);
            } finally {
              close();
            }
          },
        });
      })}
    >
      <Box ref={headerRef}>
        <Group
          justify="space-between"
          mb="xs"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 10,
          }}
        >
          <Badge
            variant="gradient"
            gradient={{ from: "blue", to: "cyan", deg: 90 }}
          >
            {isDetail
              ? `Chi tiết phiếu xuất kho bảo hành NCC${outwardInfo?.transactionCode ? ` - ${outwardInfo.transactionCode}` : ""}`
              : "Xuất kho bảo hành NCC"}
          </Badge>
          <Flex gap="sm">
            <Button
              type="button"
              variant="default"
              size="sm"
              radius="md"
              loading={visible}
              leftSection={!visible ? <IconArrowBackUp size={16} stroke={1.5} /> : undefined}
              onClick={() => back(false)}
            >
              Quay lại
            </Button>
            {!isDetail && (
              <Button
                type="submit"
                color="blue"
                variant="filled"
                size="sm"
                radius="md"
                loading={visible}
                disabled={isSaved}
                leftSection={!visible && !isSaved ? <IconCheck size={16} stroke={1.5} /> : undefined}
              >
                {isSaved ? "Đã lưu phiếu" : "Lưu phiếu"}
              </Button>
            )}
          </Flex>
        </Group>
      </Box>
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <Box ref={formRef}>
      <Grid mt={5} gutter="md">
        {/* ===== CỘT TRÁI: THÔNG TIN BÊN XUẤT ===== */}
        <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
          <Paper p="md" radius="md" withBorder style={{ backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <Group gap="xs" mb="sm" pb="xs" style={{ borderBottom: "1px solid #f1f5f9" }}>
              <ThemeIcon color="blue" variant="light" size="md" radius="sm">
                <IconPackageExport size={18} />
              </ThemeIcon>
              <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                Thông tin bên xuất
              </Text>
            </Group>
            <Grid gutter="xs">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Mã phiếu"
                  size="xs"
                  value={outwardInfo?.transactionCode || ""}
                  variant="filled"
                  readOnly
                  fw={600}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <DatePickerInput
                  label="Ngày lập"
                  size="xs"
                  value={
                    outwardInfo?.createDate
                      ? new Date(outwardInfo.createDate)
                      : form.getValues().createDate
                      ? new Date(form.getValues().createDate ?? "")
                      : null
                  }
                  valueFormat="DD-MM-YYYY"
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Chi nhánh"
                  size="xs"
                  value={
                    outwardInfo?.branchName ||
                    (firstItem?.invOrgCode && firstItem?.invOrgName
                      ? `${firstItem.invOrgCode} - ${firstItem.invOrgName}`
                      : firstItem?.invOrgCode) ||
                    ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Kho"
                  size="xs"
                  value={
                    firstItem?.subInvCode && firstItem?.subInvName
                      ? `${firstItem.subInvCode} - ${firstItem.subInvName}`
                      : firstItem?.subInvName || ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Tên người xuất"
                  size="xs"
                  value={outwardInfo?.creatorName || userLogin?.fullName || ""}
                  variant="filled"
                  readOnly
                  leftSection={<IconUser size={14} />}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Bộ phận"
                  size="xs"
                  value={outwardInfo?.departmentName || userLogin?.departmentName || ""}
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Textarea
                  label="Ghi chú"
                  placeholder="Nhập ghi chú"
                  size="xs"
                  minRows={5}
                  maxRows={5}
                  autosize
                  readOnly={isDetail}
                  variant={isDetail ? "filled" : "default"}
                  {...textFieldProps("description")}
                />
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>

        {/* ===== CỘT PHẢI: THÔNG TIN BÊN NHẬN ===== */}
        <Grid.Col span={{ base: 12, md: 12, lg: 6 }}>
          <Paper p="md" radius="md" withBorder style={{ backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <Group gap="xs" mb="sm" pb="xs" style={{ borderBottom: "1px solid #f1f5f9" }}>
              <ThemeIcon color="teal" variant="light" size="md" radius="sm">
                <IconPackageImport size={18} />
              </ThemeIcon>
              <Text fw={600} size="sm" style={{ color: "#1e293b" }}>
                Thông tin bên nhận
              </Text>
            </Group>
            <Grid gutter="xs">
              <Grid.Col span={12}>
                <Select
                  label="Nhà cung cấp"
                  placeholder="Nhập tên NCC"
                  searchable
                  clearable
                  size="xs"
                  disabled={isDetail}
                  data={dataVendorSelect?.map((item: any) => ({
                    value: item.value,
                    label: item.label,
                  }))}
                  nothingFoundMessage="Không tìm thấy dữ liệu !"
                  {...selectFieldProps("customerId")}
                  withAsterisk
                  onChange={(e) => {
                    form.setValues((prev) => ({
                      ...prev,
                      customerId: e ? Number(e) : null,
                      customerName: e
                        ? getValueById(e?.toString(), dataVendorSelect, "label")
                        : null,
                      telephoneNumber: e
                        ? getValueById(e?.toString(), dataVendorSelect, "phone")
                        : null,
                      toAddress: e
                        ? getValueById(e?.toString(), dataVendorSelect, "addr")
                        : null,
                    }));
                    setCodeSupplier(
                      e
                        ? getValueById(e?.toString(), dataVendorSelect, "code")
                        : "",
                    );
                  }}
                  onClick={() => {
                    if (dataVendorSelect.length === 0) {
                      getDataVendor();
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Địa chỉ NCC"
                  size="xs"
                  value={
                    form.getValues().toAddress
                      ? form.getValues().toAddress?.toString()
                      : ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <TextInput
                  label="Số điện thoại NCC"
                  size="xs"
                  value={
                    form.getValues().telephoneNumber
                      ? form.getValues().telephoneNumber?.toString()
                      : ""
                  }
                  variant="filled"
                  readOnly
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Bộ phận giao hàng"
                  placeholder="Chọn bộ phận giao hàng"
                  size="xs"
                  disabled={isDetail}
                  data={[
                    { label: "Không", value: "KHONG" },
                    { label: "Nội bộ", value: "NOI_BO" },
                    { label: "GHTK", value: "GHTK" },
                    { label: "Nhất tín", value: "NHAT_TIN" },
                    { label: "SCA", value: "SCA" },
                    { label: "Tín thành", value: "TIN_THANH" },
                    { label: "Viettel", value: "VIETTEL" },
                    { label: "VN Post", value: "VN_POST" },
                  ]}
                  value={form.getValues().departmentId}
                  searchable
                  nothingFoundMessage={"Không tìm thấy dữ liệu !"}
                  withAsterisk
                  {...form.getInputProps("departmentId")}
                  onChange={(e) => {
                    form.setValues((prev) => ({
                      ...prev,
                      departmentId: e ? e : "KHONG",
                      priority: e !== "NOI_BO" ? null : prev.priority,
                    }));
                    setIsShipping(e === "NOI_BO");
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Select
                  label="Mức độ ưu tiên"
                  placeholder="Chọn mức độ ưu tiên"
                  size="xs"
                  data={dataDeliverPriority}
                  value={
                    form.getValues().priority
                      ? form.getValues()?.priority?.toString()
                      : null
                  }
                  searchable
                  clearable
                  nothingFoundMessage={"Không tìm thấy dữ liệu !"}
                  disabled={isDetail || form.getValues().departmentId === "KHONG"}
                  onChange={(e) =>
                    form.setValues((prev) => ({
                      ...prev,
                      priority: e ? e : null,
                    }))
                  }
                  onClick={() => {
                    if (dataDeliverPriority.length === 0) {
                      getDataDeliverPrioritySelect();
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <Textarea
                  label="Nội dung giao hàng"
                  placeholder="Nhập nội dung giao hàng"
                  size="xs"
                  minRows={5}
                  maxRows={5}
                  autosize
                  readOnly={isDetail}
                  variant={isDetail ? "filled" : "default"}
                  {...textFieldProps("content")}
                />
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>
      </Grid>
      </Box>

      {/* Table */}
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default ServiceRepairOutWardWarrantyVendor;
